import type { SessionRankingOutput } from '../types/prioritization/session';

export type MatrixReportVariant = 'impact_effort' | 'eisenhower' | 'moscow';

export interface QuadrantMeta {
  key: string;
  label: string;
  subtitle: string;
  color: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
}

export const IMPACT_EFFORT_QUADRANTS: QuadrantMeta[] = [
  {
    key: 'quick-wins',
    label: 'Quick wins',
    subtitle: 'High impact · Low effort',
    color: '#22c55e',
    bgClass: 'bg-[var(--green-dim)]',
    borderClass: 'border-[var(--green)]',
    textClass: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  },
  {
    key: 'big-bets',
    label: 'Big bets',
    subtitle: 'High impact · High effort',
    color: '#f59e0b',
    bgClass: 'bg-[var(--mustard-dim)]',
    borderClass: 'border-[var(--mustard)]',
    textClass: 'text-[var(--mustard)]',
  },
  {
    key: 'fill-ins',
    label: 'Fill-ins',
    subtitle: 'Low impact · Low effort',
    color: '#94a3b8',
    bgClass: 'bg-[var(--surface)]',
    borderClass: 'border-[var(--rule)]',
    textClass: 'text-[var(--text-sec)]',
  },
  {
    key: 'avoid',
    label: 'Avoid',
    subtitle: 'Low impact · High effort',
    color: '#ef4444',
    bgClass: 'bg-[var(--coral-dim)]',
    borderClass: 'border-[var(--coral)]',
    textClass: 'text-[var(--coral)]',
  },
];

export const EISENHOWER_QUADRANTS: QuadrantMeta[] = [
  {
    key: 'ui',
    label: 'Do',
    subtitle: 'Urgent + Important',
    color: '#ef4444',
    bgClass: 'bg-[var(--coral-dim)]',
    borderClass: 'border-[var(--coral)]',
    textClass: 'text-[var(--coral)]',
  },
  {
    key: 'in',
    label: 'Schedule',
    subtitle: 'Important, not urgent',
    color: '#f59e0b',
    bgClass: 'bg-[var(--mustard-dim)]',
    borderClass: 'border-[var(--mustard)]',
    textClass: 'text-[var(--mustard)]',
  },
  {
    key: 'un',
    label: 'Delegate',
    subtitle: 'Urgent, not important',
    color: '#3b82f6',
    bgClass: 'bg-[var(--slate-dim)]',
    borderClass: 'border-[var(--slate)]',
    textClass: 'text-[var(--text-sec)]',
  },
  {
    key: 'nn',
    label: 'Eliminate',
    subtitle: 'Not urgent, not important',
    color: '#94a3b8',
    bgClass: 'bg-[var(--surface)]',
    borderClass: 'border-[var(--rule)]',
    textClass: 'text-[var(--text-sec)]',
  },
];

export const MOSCOW_TIERS: QuadrantMeta[] = [
  { key: 'must', label: 'Must have', subtitle: '', color: '#ef4444', bgClass: 'bg-[var(--coral-dim)]', borderClass: 'border-[var(--coral)]', textClass: 'text-[var(--coral)]' },
  { key: 'should', label: 'Should have', subtitle: '', color: '#f59e0b', bgClass: 'bg-[var(--mustard-dim)]', borderClass: 'border-[var(--mustard)]', textClass: 'text-[var(--mustard)]' },
  { key: 'could', label: 'Could have', subtitle: '', color: '#3b82f6', bgClass: 'bg-[var(--slate-dim)]', borderClass: 'border-[var(--slate)]', textClass: 'text-[var(--text-sec)]' },
  { key: 'wont', label: "Won't have", subtitle: '', color: '#94a3b8', bgClass: 'bg-[var(--surface)]', borderClass: 'border-[var(--rule)]', textClass: 'text-[var(--text-sec)]' },
];

export function resolveMatrixReportVariant(toolId: string | null | undefined): MatrixReportVariant | null {
  const t = (toolId ?? '').toLowerCase();
  if (t.includes('impact') && t.includes('effort')) return 'impact_effort';
  if (t.includes('eisenhower')) return 'eisenhower';
  if (t.includes('moscow')) return 'moscow';
  return null;
}

export function quadrantMetaForVariant(variant: MatrixReportVariant): QuadrantMeta[] {
  switch (variant) {
    case 'impact_effort': return IMPACT_EFFORT_QUADRANTS;
    case 'eisenhower': return EISENHOWER_QUADRANTS;
    case 'moscow': return MOSCOW_TIERS;
  }
}

export function quadrantMetaByKey(variant: MatrixReportVariant, key: string): QuadrantMeta | undefined {
  return quadrantMetaForVariant(variant).find(q => q.key === key);
}

/** Resolve per-item bucket from ranking output (classifications, tiers, or DB classification). */
export function resolveItemBucket(
  output: SessionRankingOutput | null | undefined,
  itemId: string,
  dbClassification?: string | null,
): string | null {
  if (dbClassification) return dbClassification;
  const tier = output?.tiers?.[itemId];
  if (tier) return tier;
  const cl = output?.classifications?.[itemId];
  if (!cl) return null;
  if (typeof cl === 'string') return cl;
  return (cl as { primaryCategory?: string }).primaryCategory ?? null;
}

export function resolveItemAgreement(
  output: SessionRankingOutput | null | undefined,
  itemId: string,
): number | null {
  const cl = output?.classifications?.[itemId];
  if (!cl || typeof cl === 'string') return null;
  const agreement = (cl as { agreement?: number }).agreement;
  return typeof agreement === 'number' ? agreement : null;
}

export function formatBucketLabel(variant: MatrixReportVariant, bucket: string): string {
  return quadrantMetaByKey(variant, bucket)?.label ?? bucket.replace(/-/g, ' ');
}
